// src/components/OwnerTickerManager.jsx
// Owner dashboard component for controlling the global ticker message
// Creates/updates the system/ticker document in Firestore

import React, { useState, useEffect } from 'react';
import { db } from '../utils/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const OwnerTickerManager = () => {
  const [message, setMessage] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [showIcon, setShowIcon] = useState(true);
  const [allowDismiss, setAllowDismiss] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);

  // Character limit for the message
  const MAX_CHARS = 150;

  // Load current ticker settings
  useEffect(() => {
    const loadTickerSettings = async () => {
      try {
        const tickerRef = doc(db, 'system', 'ticker');
        const tickerDoc = await getDoc(tickerRef);
        
        if (tickerDoc.exists()) {
          const data = tickerDoc.data();
          setMessage(data.message || '');
          setIsActive(data.isActive || false);
          setShowIcon(data.showIcon !== false);
          setAllowDismiss(data.allowDismiss !== false);
          setLastUpdated(data.updatedAt?.toDate?.() || null);
        }
      } catch (error) {
        console.error('Error loading ticker settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTickerSettings();
  }, []);

  // Save ticker settings
  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);

    try {
      const tickerRef = doc(db, 'system', 'ticker');
      await setDoc(tickerRef, {
        message: message.trim(),
        isActive,
        showIcon,
        allowDismiss,
        updatedAt: serverTimestamp(),
      });

      setLastUpdated(new Date());
      setSaveStatus('success');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving ticker settings:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Quick action: Turn off ticker
  const handleTurnOff = async () => {
    setSaving(true);
    try {
      const tickerRef = doc(db, 'system', 'ticker');
      await setDoc(tickerRef, {
        message: message.trim(),
        isActive: false,
        showIcon,
        allowDismiss,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setIsActive(false);
      setLastUpdated(new Date());
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error turning off ticker:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Quick action: Turn on ticker
  const handleTurnOn = async () => {
    if (!message.trim()) {
      setSaveStatus('error');
      return;
    }
    
    setSaving(true);
    try {
      const tickerRef = doc(db, 'system', 'ticker');
      await setDoc(tickerRef, {
        message: message.trim(),
        isActive: true,
        showIcon,
        allowDismiss,
        updatedAt: serverTimestamp(),
      });

      setIsActive(true);
      setLastUpdated(new Date());
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error turning on ticker:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading ticker settings...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>📺 Global Ticker</h2>
          <span style={{
            ...styles.statusBadge,
            backgroundColor: isActive ? '#dcfce7' : '#f3f4f6',
            color: isActive ? '#166534' : '#6b7280',
          }}>
            {isActive ? '● LIVE' : '○ OFF'}
          </span>
        </div>
        {lastUpdated && (
          <span style={styles.lastUpdated}>
            Last updated: {lastUpdated.toLocaleString()}
          </span>
        )}
      </div>

      {/* Preview */}
      <div style={styles.previewSection}>
        <label style={styles.label}>Preview</label>
        <div style={styles.preview}>
          <div style={styles.previewScoreboard}>
            <div style={styles.previewLedOverlay} />
            {!message.trim() ? (
              <span style={styles.previewPlaceholder}>Enter a message below...</span>
            ) : (
              <div style={styles.previewContent}>
                {showIcon && <span style={styles.previewIcon}>📢</span>}
                <span style={styles.previewMessage}>{message}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Message Input */}
      <div style={styles.inputSection}>
        <label style={styles.label}>
          Message
          <span style={styles.charCount}>
            {message.length}/{MAX_CHARS}
          </span>
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
          placeholder="Enter your ticker message..."
          style={styles.textarea}
          rows={2}
        />
      </div>

      {/* Options */}
      <div style={styles.optionsSection}>
        <label style={styles.label}>Options</label>
        <div style={styles.optionsGrid}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showIcon}
              onChange={(e) => setShowIcon(e.target.checked)}
              style={styles.checkbox}
            />
            Show 📢 icon
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={allowDismiss}
              onChange={(e) => setAllowDismiss(e.target.checked)}
              style={styles.checkbox}
            />
            Allow users to dismiss
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={styles.actions}>
        <div style={styles.quickActions}>
          {isActive ? (
            <button
              onClick={handleTurnOff}
              disabled={saving}
              style={styles.turnOffButton}
            >
              ⏹ Turn Off Now
            </button>
          ) : (
            <button
              onClick={handleTurnOn}
              disabled={saving || !message.trim()}
              style={{
                ...styles.turnOnButton,
                opacity: (!message.trim()) ? 0.5 : 1,
                cursor: (!message.trim()) ? 'not-allowed' : 'pointer',
              }}
            >
              ▶ Go Live Now
            </button>
          )}
        </div>
        
        <button
          onClick={handleSave}
          disabled={saving}
          style={styles.saveButton}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Status Message */}
      {saveStatus && (
        <div style={{
          ...styles.statusMessage,
          backgroundColor: saveStatus === 'success' ? '#dcfce7' : '#fee2e2',
          color: saveStatus === 'success' ? '#166534' : '#dc2626',
        }}>
          {saveStatus === 'success' 
            ? '✓ Ticker settings saved successfully!' 
            : '✕ Error saving settings. Please try again.'}
        </div>
      )}

      {/* Usage Tips */}
      <div style={styles.tips}>
        <h4 style={styles.tipsTitle}>💡 Tips</h4>
        <ul style={styles.tipsList}>
          <li>Keep messages short and punchy - they appear in uppercase</li>
          <li>Use for announcements, maintenance notices, or special offers</li>
          <li>The ticker appears at the bottom of every page when active</li>
          <li>Users can dismiss the ticker (unless disabled)</li>
        </ul>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    maxWidth: '600px',
  },
  loadingState: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  lastUpdated: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  previewSection: {
    marginBottom: '20px',
  },
  label: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
  },
  charCount: {
    fontSize: '12px',
    color: '#9ca3af',
    fontWeight: '400',
  },
  preview: {
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  previewScoreboard: {
    backgroundColor: '#0a0a0a',
    background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 50%, #000000 100%)',
    padding: '12px 16px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  previewLedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'radial-gradient(circle, transparent 1px, #000 1px)',
    backgroundSize: '3px 3px',
    opacity: 0.3,
    pointerEvents: 'none',
  },
  previewPlaceholder: {
    color: '#666',
    fontSize: '14px',
    fontFamily: '"Courier New", Courier, monospace',
  },
  previewContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  previewIcon: {
    fontSize: '16px',
  },
  previewMessage: {
    color: '#FFD700',
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    textShadow: '0 0 10px #FFD700, 0 0 20px #FFA500',
  },
  inputSection: {
    marginBottom: '20px',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  optionsSection: {
    marginBottom: '24px',
  },
  optionsGrid: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#374151',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  quickActions: {
    display: 'flex',
    gap: '8px',
  },
  turnOnButton: {
    padding: '10px 20px',
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  turnOffButton: {
    padding: '10px 20px',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#243665',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  statusMessage: {
    marginTop: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },
  tips: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  tipsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    margin: '0 0 8px 0',
  },
  tipsList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.6',
  },
};

export default OwnerTickerManager;
